import { ReactElement } from 'react';
import { isNullish } from 'remeda';
import { NormalizedCustomAttributeDefinition } from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import { getAttributeOptionLabel } from 'src/graphql/entities/custom-attributes/field-view-registry/utils';
import {
  parseStoredAttributes,
  resolveStoredValue,
} from 'src/graphql/entities/custom-attributes/payload';
import { SortingInput } from 'src/graphql/types';
import { toStringArray } from 'src/utils/objects';
import { CustomAttributeCell } from './cell';
import { buildCustomAttributeColumnId, parseCustomAttributeJsonPath } from './identifiers';

// a grid row carrying the raw custom_attributes blob: the same normalized cache field the side panel
// editor reads and writes. reading display values from it (instead of a separate dynamicAttributes
// projection) means an edit in the panel reflects in the grid with no projection to reconcile, and
// there is no second field to go stale across a persisted-cache reload. kept structural so the
// generic grid table can pass any record without coupling to a concrete entity type.
export interface CustomAttributesRow {
  customAttributes?: string | null;
}

// how the grid table reads and renders one custom attribute column off a row's blob.
export interface CustomAttributeColumnRenderer {
  // string form for the table's accessorFn (client sort / global-filter fallback) and CSV export.
  getStringValue: (row: CustomAttributesRow) => string;
  // decoded cell, identical to the side panel (same field-view registry and value resolution).
  renderCell: (row: CustomAttributesRow) => ReactElement;
}

// the definition default acts as the placeholder for an unset key, exactly as the side panel does.
function resolveRawValue(
  definition: NormalizedCustomAttributeDefinition,
  row: CustomAttributesRow,
): unknown {
  const { values } = parseStoredAttributes(row.customAttributes);
  return resolveStoredValue(values, definition.key, definition.defaultValue);
}

function getCustomAttributeText(
  definition: NormalizedCustomAttributeDefinition,
  value: unknown,
): string {
  if (definition.type === 'dropdown') {
    return typeof value === 'string' ? getAttributeOptionLabel(definition.options, value) : '';
  }

  if (definition.type === 'multi_select') {
    return toStringArray(value)
      .map((key) => getAttributeOptionLabel(definition.options, key))
      .join(', ');
  }

  if (definition.type === 'checkbox') {
    return value === true ? 'Yes' : 'No';
  }

  if (value === null || value === undefined) {
    return '';
  }

  return Array.isArray(value) ? value.join(', ') : String(value);
}

/**
 * Builds the per-column renderers keyed by the column id (`customAttribute__<key>`), so the table can
 * look one up for a selected custom column and bypass the default `pathOr` accessor / general cell.
 */
export function buildCustomAttributeColumnRenderers(
  defs: NormalizedCustomAttributeDefinition[],
): Map<string, CustomAttributeColumnRenderer> {
  return new Map(
    defs.map((def) => {
      const renderer: CustomAttributeColumnRenderer = {
        getStringValue: (row) => getCustomAttributeText(def, resolveRawValue(def, row)),
        renderCell: (row) => (
          <CustomAttributeCell value={resolveRawValue(def, row)} definition={def} />
        ),
      };

      return [buildCustomAttributeColumnId(def.key), renderer];
    }),
  );
}

/**
 * client-side sort value for a custom attribute SortingInput, mirroring the server's typed compare
 * and coalesce to default (see getGuardedCastExpression). lets the cached re-sort (sortByInput)
 * reorder on an in-place edit, e.g. toggling a checkbox, without a refetch. returns undefined
 * for any non-custom-attribute sort key so the caller falls back to its normal field lookup.
 */
export function resolveCustomAttributeSortValue(
  row: CustomAttributesRow,
  sortItem: SortingInput,
): number | string | undefined {
  const parsed = parseCustomAttributeJsonPath(sortItem.attribute);
  if (!parsed) {
    return undefined;
  }

  const { values } = parseStoredAttributes(row.customAttributes);
  // an unset key reads as the definition default (the same value the cell renders), matching the
  // server's coalesce; a present key wins.
  const raw = resolveStoredValue(values, parsed.key, sortItem.coalesceDefault);

  // coerce to the ordering the server compares on (CUSTOM_ATTRIBUTE_GRID_BEHAVIOR_BY_TYPE casts).
  if (parsed.cast === '::boolean') {
    // 0 or 1 so the re-sort orders false before true, matching bool_or asc/desc.
    return raw === true || raw === 'true' ? 1 : 0;
  }

  // empty custom-attribute values sink to the bottom in BOTH directions, mirroring the server's
  // nulls-last for custom-attribute columns (see addSorting in sort.builder.ts)
  const isEmpty = isNullish(raw) || raw === '';

  if (parsed.cast === '::numeric') {
    const numeric = Number(raw);
    if (!isEmpty && Number.isFinite(numeric)) {
      return numeric;
    }

    return sortItem.desc ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
  }

  // text cast + the date sort cast (ISO text sorts chronologically).
  if (isEmpty) {
    return sortItem.desc ? '' : String.fromCharCode(0xffff);
  }

  return String(raw);
}
